import { Button } from "@components/Buttons"
import FieldControl from "@components/FieldControl"
import { Input, Select, Textarea } from "@components/FormFields"
import { useIntl } from "@components/Intl"
import { Box } from "@components/Layout"
import { useNotificationsUpdate } from "@components/Notifications"
import { ReturnOrderRequestMutationVariables } from "@generated"
import { getGraphQLClient } from "@lib/graphql-client"
import { useMutation } from "@tanstack/react-query"
import { Form as FormikForm, Formik, FormikProps } from "formik"
import { rem } from "polished"
import React from "react"
import * as Yup from "yup"
import * as S from "./elements"

interface FormValues {
  email: string
  orderNumber: string
  productIds: string
  reason: string
}

const { sdk } = getGraphQLClient()

const Form: React.FC = () => {
  const { mutateAsync, isLoading } = useMutation(
    async ({ input }: ReturnOrderRequestMutationVariables) => {
      return await sdk.ReturnOrderRequest({
        input,
      })
    }
  )
  const { messages } = useIntl()

  const schema = Yup.object().shape({
    email: Yup.string()
      .required(
        `${messages.returnForm.email} ${messages.returnForm.isRequired}`
      )
      .email(messages.returnForm.emailError),
    orderNumber: Yup.string().required(
      `${messages.returnForm.orderNumber} ${messages.returnForm.isRequired}`
    ),
    productIds: Yup.string().required(
      `${messages.returnForm.productIds} ${messages.returnForm.isRequired}`
    ),
    reason: Yup.string().required(
      `${messages.returnForm.reason} ${messages.returnForm.isRequired}`
    ),
  })

  const { addNotification } = useNotificationsUpdate()

  return (
    <Formik
      validateOnBlur
      validateOnChange={false}
      initialValues={{
        email: "",
        orderNumber: "",
        productIds: "",
        reason: "",
      }}
      validationSchema={schema}
      onSubmit={async (values, { resetForm }) => {
        const productIds = values.productIds
          .split(",")
          .map((p) => p.trim())
          .filter((v) => v)
        const orderId = parseInt(values.orderNumber, 10)
        try {
          await mutateAsync({
            input: {
              email: values.email,
              items: productIds.map((id) => ({
                id,
                reason: values.reason,
              })),
              orderId,
            },
          })
          addNotification({
            type: "success",
            text: messages.returnForm.submissionReturnSuccess,
          })
          resetForm()
        } catch (error) {
          addNotification({
            type: "error",
            text: messages.returnForm.submissionReturnFailed,
          })
        }
      }}
    >
      {({
        errors,
        touched,
        handleBlur,
        handleChange,
        values,
      }: FormikProps<FormValues>) => (
        <FormikForm>
          <S.FormWrap isLoading={isLoading} maxWidth={["100%", rem(500)]}>
            <FieldControl label={messages.returnForm.email} maxWidth="100%">
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                name="email"
                placeholder={messages.returnForm.email}
                errorMsg={
                  errors.email && touched.email ? errors.email : undefined
                }
              />
            </FieldControl>
            <FieldControl
              label={messages.returnForm.orderNumber}
              maxWidth="100%"
            >
              <Input
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.orderNumber}
                name="orderNumber"
                placeholder={messages.returnForm.orderNumber}
                errorMsg={
                  errors.orderNumber && touched.orderNumber
                    ? errors.orderNumber
                    : undefined
                }
              />
            </FieldControl>
            <FieldControl
              label={messages.returnForm.productIds}
              sublabel={messages.returnForm.productIdsSub}
              maxWidth="100%"
              gridTemplateColumns="100%"
            >
              <Textarea
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.productIds}
                placeholder="e.g. 1234,5678"
                name="productIds"
                errorMsg={
                  errors.productIds && touched.productIds
                    ? errors.productIds
                    : undefined
                }
              />
            </FieldControl>
            <FieldControl label={messages.returnForm.reason} maxWidth="100%">
              <Select
                name="reason"
                value={values.reason}
                onChange={handleChange}
                onBlur={handleBlur}
                hasError={typeof errors.reason !== "undefined"}
              >
                <option value="" key="select">
                  {messages.returnForm.select}
                </option>
                {messages.returnForm.reasons.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </FieldControl>

            <Box py={3} display="flex" justifyContent="flex-end">
              <Button
                maxWidth={200}
                variant="purple"
                title={messages.returnForm.submitForm}
                type="submit"
                state={isLoading ? "loading" : undefined}
              >
                <span>{messages.returnForm.submit}</span>
              </Button>
            </Box>
          </S.FormWrap>
        </FormikForm>
      )}
    </Formik>
  )
}

export default Form
